import { useState, useEffect, useCallback } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { pushStepAtom } from "store/signUpStore";
import { userStore } from "store/userStore";

interface UseCheckDuplicateCtnOptions {
    /** 에러 발생 시 이동할 step (기본: 8) */
    errorStep?: number;
}

/**
 * CTN 중복 검사 훅
 * - 중복 → Step7
 * - 중복 없음 → Step6
 * - 에러 → errorStep (기본 8)
 */
export const useCheckDuplicateCtn = (options?: UseCheckDuplicateCtnOptions) => {
    const pushStep = useSetAtom(pushStepAtom);
    const checkDuplicateCtnState = useAtomValue(userStore.checkDuplicateCtn.stateAtom);
    const [isCheckingCtn, setIsCheckingCtn] = useState(false);
    const errorStep = options?.errorStep ?? 8;

    useEffect(() => {
        if (checkDuplicateCtnState.status === "ready" && isCheckingCtn) {
            setIsCheckingCtn(false);
            const data = checkDuplicateCtnState.data;

            if (data?.result === "duplicate") {
                // 중복 계정 존재 → Step7 (onDefault는 Step7에서 처리)
                pushStep(7);
            } else {
                // 중복 없음 → Step6
                userStore.checkDuplicateCtn.onDefault();
                pushStep(6);
            }
        } else if (checkDuplicateCtnState.status === "error" && isCheckingCtn) {
            setIsCheckingCtn(false);
            userStore.checkDuplicateCtn.onDefault();
            pushStep(errorStep);
        }
    }, [checkDuplicateCtnState.status, checkDuplicateCtnState.data, isCheckingCtn, pushStep, errorStep]);

    /** CTN 중복 검사 요청 */
    const checkCtn = useCallback((params: {
        serviceId: string;
        ctnCountryCd: string;
        ctn: string;
    }) => {
        // 이전 호출의 잔존 상태(특히 Step7 → 뒤로 → 재인증 시 'ready' 로 남은 응답)를
        //   먼저 리셋 — 안 하면 isCheckingCtn=true 로 바뀌는 순간 stale 응답으로 즉시 오분기.
        userStore.checkDuplicateCtn.onDefault();
        setIsCheckingCtn(true);
        userStore.onCheckDuplicateCtn(params);
    }, []);

    /**
     * 인증 스텝으로 이동
     * - KR → step3 (이름·통신사·번호 입력 → 다날)
     * - 해외 → step4 (국가코드·번호 입력 → SMS OTP)
     *
     * SNS 가입자도 예외 없이 인증을 거친다. SNS 가 제공하는 이름/생년월일/성별/전화번호는
     * 카카오·네이버 계정의 임의 입력값이라 본인확인 결과가 아니며(카카오 공식 안내상 CI 로도
     * 본인확인을 대체할 수 없음), 검증 없이 신뢰하면 미검증 번호가 그대로 계정 식별자가 된다.
     * SNS 값은 각 입력 폼의 prefill 로만 쓰고, 확정은 인증 결과로 한다.
     */
    const navigateToAuth = useCallback((countryCd: string) => {
        pushStep(countryCd === "KR" ? 3 : 4);
    }, [pushStep]);

    return { checkCtn, navigateToAuth, isCheckingCtn };
};
